// Approximated collapsed size so that there are ~three lines of text
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import truncate from 'lodash/truncate';
import { memo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

// in the desktop layout in the host section of the ListingPageComponent.

const BIO_COLLAPSED_LENGTH = 170;

// Shared .mobileBio/.desktopBio base + textAlign carried over from the original sx.
const bioBaseSx = (theme) => ({
  textAlign: { xs: 'center', md: 'left' },
  // Preserve newlines, but collapse other whitespace
  whiteSpace: 'pre-line',
  margin: '18px 0 0 0',
  transition: 'all 1s ease-in',
  ...theme.mixins.marketplaceDefaultFontStyles,
  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceDefaultFontStyles[theme.breakpoints.up('md')],
    margin: '18px 0 0 0',
  },
});

// .desktopBio (display:none, md → margin-top:16px; display:block)
const desktopBioSx = (theme) => ({
  ...bioBaseSx(theme),
  display: 'none',
  [theme.breakpoints.up('md')]: {
    ...bioBaseSx(theme)[theme.breakpoints.up('md')],
    marginTop: '16px',
    display: 'block',
  },
});

// .mobileBio (md → display:none)
const mobileBioSx = (theme) => ({
  ...bioBaseSx(theme),
  [theme.breakpoints.up('md')]: {
    ...bioBaseSx(theme)[theme.breakpoints.up('md')],
    display: 'none',
  },
});

// .translationNote
const translationNoteSx = (theme) => ({
  ...theme.mixins.marketplaceSmallFontStyles,
  color: theme.tokens.matterColorAnti,
  marginTop: '12px',
  textAlign: 'right',
});

const truncated = (s) => {
  return truncate(s, {
    length: BIO_COLLAPSED_LENGTH,

    // Allow truncated text end only in specific characters. This will
    // make the truncated text shorter than the length if the original
    // text has to be shortened and the substring ends in a separator.
    //
    // This ensures that the final text doesn't get cut in the middle
    // of a word.
    separator: /\s|,|\.|:|;/,
    omission: '…',
  });
};

const ExpandableBioComponent = ({ variant = 'desktop', bio = null, isTranslated = false }) => {
  const [expand, setExpand] = useState(false);
  const truncatedBio = truncated(bio);

  const handleShowMoreClick = () => {
    setExpand(true);
  };

  return (
    <Box>
      <Typography sx={variant === 'mobile' ? mobileBioSx : desktopBioSx} variant="body1">
        {expand ? bio : truncatedBio}
        {!expand && bio !== truncatedBio ? (
          <Button onClick={handleShowMoreClick}>
            <FormattedMessage id="UserCard.showFullBioLink" />
          </Button>
        ) : null}
      </Typography>
      {isTranslated ? (
        <Typography sx={translationNoteSx} variant="body1">
          <FormattedMessage id="ExpandableBio.isTranslated" />
        </Typography>
      ) : null}
    </Box>
  );
};

ExpandableBioComponent.displayName = 'ExpandableBio';

const ExpandableBio = memo(ExpandableBioComponent);

export default ExpandableBio;
